import { FC } from "react";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { TreatmentPlan } from "../../domain/treatmentPlan.interface";

interface TreatmentPlansListProps {
	treatmentPlans?: TreatmentPlan[];
	/**
	 * SIMS-9035 AC5 — plans with a threshold change in progress. Marked here so the refusal the
	 * administrator would meet at save time is never a surprise.
	 */
	propagatingPlanIds?: string[];
	events?: Array<{ _id: string; name: string }>;
	onEdit: (plan: TreatmentPlan) => void;
	onClone: (plan: TreatmentPlan) => void;
	onDelete: (planId: string) => void;
}

const TreatmentPlansList: FC<TreatmentPlansListProps> = ({
	treatmentPlans,
	propagatingPlanIds = [],
	events,
	onEdit,
	onClone,
	onDelete,
}) => {
	// Helper function to format period length for display
	const formatPeriodLength = (periodLength: number): string => {
		if (periodLength === 1) return "Weekly";
		if (periodLength === 4.348) return "Monthly";
		if (periodLength === 2) return "Bi-weekly";
		if (periodLength === 4) return "Every 4 weeks";
		return `${periodLength} weeks`;
	};

	if (!treatmentPlans || treatmentPlans.length === 0) {
		return (
			<Typography color="text.secondary">
				No treatment plans found. Create your first treatment plan using the
				button above.
			</Typography>
		);
	}

	return (
		<Grid container spacing={3}>
			{treatmentPlans.map((plan) => (
				<Grid item xs={12} md={6} lg={4} key={plan._id}>
					<Paper sx={{ p: 2, position: "relative" }}>
						{/* Action buttons */}
						<Box
							sx={{
								position: "absolute",
								top: 8,
								right: 8,
								display: "flex",
								gap: 1,
							}}
						>
							<IconButton
								size="small"
								onClick={() => onEdit(plan)}
								title="Edit treatment plan"
							>
								<EditIcon fontSize="small" />
							</IconButton>
							<IconButton
								size="small"
								color="primary"
								onClick={() => onClone(plan)}
								title="Clone treatment plan"
							>
								<ContentCopyIcon fontSize="small" />
							</IconButton>
							<IconButton
								size="small"
								color="error"
								onClick={() => onDelete(plan._id)}
								title="Delete treatment plan"
							>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</Box>

						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								// The chips carry long labels ("Threshold change in progress"), and
								// without wrapping they win the flex negotiation and squeeze the plan
								// name down to one word per line. Wrapping moves them to a second row
								// instead of deforming the heading.
								flexWrap: "wrap",
								gap: 1,
								mb: 1,
								pr: 8,
							}}
						>
							<Typography variant="h6">
								{plan.name || "Treatment Plan"}
							</Typography>
							{/* SIMS-9035 AC5: a change is being applied to this plan's participants, so a
							    further threshold change will be refused until it finishes. */}
							{propagatingPlanIds.includes(plan._id) && (
								<Chip
									label="Threshold change in progress"
									size="small"
									color="info"
									variant="outlined"
									data-testid="propagation-in-progress-chip"
									sx={{ flexShrink: 0 }}
								/>
							)}
							<Chip
								label={
									plan.programType === "SMART_PROGRAM"
										? "Smart Program"
										: "Weekly Calendar"
								}
								size="small"
								color={
									plan.programType === "SMART_PROGRAM" ? "secondary" : "default"
								}
								variant="outlined"
								sx={{ flexShrink: 0 }}
							/>
						</Box>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
							ID: {plan._id}
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
							Account: {plan.accountId}
						</Typography>
						<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
							Created: {new Date(plan.createdAt).toLocaleDateString()}
						</Typography>

						{/* Custom Plan Allowed Status */}
						<Box sx={{ mb: 2 }}>
							<Chip
								label={
									plan.customPlanAllowed
										? "Custom Plans Allowed"
										: "Template Only"
								}
								color={plan.customPlanAllowed ? "success" : "default"}
								size="small"
							/>
						</Box>

						{plan.treatmentConfigOptions && (
							<Box>
								{/* Treatment Config Templates */}
								{plan.treatmentConfigOptions.treatmentConfigTemplates &&
									plan.treatmentConfigOptions.treatmentConfigTemplates.length >
										0 && (
										<Box sx={{ mb: 1 }}>
											<Typography variant="caption" display="block">
												Templates:
											</Typography>
											<Box
												sx={{
													display: "flex",
													gap: 0.5,
												}}
											>
												{plan.treatmentConfigOptions.treatmentConfigTemplates.map(
													(template, index) => (
														<Chip
															key={index}
															label={`${template.name || `Template ${index + 1}`} (${formatPeriodLength(template.periodLength)})`}
															size="small"
															color={template.default ? "primary" : "default"}
															icon={
																template.allowCustomProviderSite ? (
																	<CheckCircleIcon />
																) : undefined
															}
														/>
													),
												)}
											</Box>
										</Box>
									)}

								{plan.treatmentConfigOptions.visitsPerPeriod &&
									plan.treatmentConfigOptions.visitsPerPeriod.length > 0 && (
										<Box sx={{ mb: 1 }}>
											<Typography variant="caption" display="block">
												Visits Per Period:
											</Typography>
											<Box
												sx={{
													display: "flex",
													gap: 0.5,
												}}
											>
												{plan.treatmentConfigOptions.visitsPerPeriod.map(
													(item, index) => (
														<Chip key={index} label={item} size="small" />
													),
												)}
											</Box>
										</Box>
									)}

								{plan.treatmentConfigOptions.duration &&
									plan.treatmentConfigOptions.duration.length > 0 && (
										<Box sx={{ mb: 1 }}>
											<Typography variant="caption" display="block">
												Duration:
											</Typography>
											<Box
												sx={{
													display: "flex",
													gap: 0.5,
												}}
											>
												{plan.treatmentConfigOptions.duration.map(
													(item, index) => (
														<Chip key={index} label={item} size="small" />
													),
												)}
											</Box>
										</Box>
									)}

								{plan.treatmentConfigOptions.behavior &&
									plan.treatmentConfigOptions.behavior.length > 0 && (
										<Box sx={{ mb: 1 }}>
											<Typography variant="caption" display="block">
												Behaviors:
											</Typography>
											<Box
												sx={{
													display: "flex",
													gap: 0.5,
												}}
											>
												{plan.treatmentConfigOptions.behavior.map(
													(behaviorId, index) => {
														const id =
															typeof behaviorId === "string"
																? behaviorId
																: (
																		behaviorId as unknown as {
																			_id?: string;
																		}
																	)?._id || "unknown";
														const event = events?.find((e) => e._id === id);
														return (
															<Chip
																key={index}
																label={event?.name || id}
																size="small"
																color="secondary"
															/>
														);
													},
												)}
											</Box>
										</Box>
									)}
							</Box>
						)}
					</Paper>
				</Grid>
			))}
		</Grid>
	);
};

export default TreatmentPlansList;
